import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, UtensilsCrossed, ShoppingBag, Phone, Gift, ChevronRight, X, Check } from 'lucide-react';
import { useKiosk } from '../context/KioskContext';
import { IconButton } from '../components/common/TouchButton';

// Import theme
import '../styles/kiosk-theme.css';

/**
 * StartOrderPage - Order type selection + optional loyalty entry
 * 
 * Flow:
 * 1. Select Dine-In or Takeout
 * 2. If Dine-In, enter table number
 * 3. Optionally enter loyalty phone
 * 4. Continue to menu
 */
export default function StartOrderPage() {
  const navigate = useNavigate();
  const {
    orderType,
    setOrderType,
    tableNumber,
    setTableNumber,
    loyaltyPhone,
    setLoyaltyPhone,
    loyaltyInfo,
    lookupLoyalty,
    clearLoyalty,
    config,
    setView,
  } = useKiosk();

  const [showTableInput, setShowTableInput] = useState(orderType === 'dine_in');
  const [showLoyaltyInput, setShowLoyaltyInput] = useState(false);
  const [loyaltyLoading, setLoyaltyLoading] = useState(false);
  const [loyaltyError, setLoyaltyError] = useState('');
  const [tempPhone, setTempPhone] = useState('');

  const restaurant = config?.restaurant || {};

  const handleBack = () => {
    navigate('..');
  };

  const handleOrderTypeSelect = (type) => {
    setOrderType(type);
    setShowTableInput(type === 'dine_in');
    if (type === 'pickup') {
      setTableNumber('');
    }
  };

  const handleTableNumberPress = (digit) => {
    if (tableNumber.length < 4) {
      setTableNumber(prev => prev + digit);
    }
  };

  const handleTableNumberClear = () => {
    setTableNumber(prev => prev.slice(0, -1));
  };

  const handleLoyaltyLookup = async () => {
    if (!tempPhone || tempPhone.length < 10) {
      setLoyaltyError('Please enter a valid phone number');
      return;
    }

    setLoyaltyLoading(true);
    setLoyaltyError('');

    const result = await lookupLoyalty(tempPhone);

    setLoyaltyLoading(false);

    if (result.success) {
      setShowLoyaltyInput(false);
    } else {
      setLoyaltyError(result.message || 'No account found');
    }
  };

  const handlePhoneInput = (value) => {
    // Format phone number
    const digits = value.replace(/\D/g, '').slice(0, 10);
    setTempPhone(digits);
  };

  const formatPhoneDisplay = (phone) => {
    if (!phone) return '';
    const digits = phone.replace(/\D/g, '');
    if (digits.length <= 3) return digits;
    if (digits.length <= 6) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
    return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
  };

  const handleContinue = () => {
    if (orderType === 'dine_in' && !tableNumber) {
      // Optionally require table number
      return;
    }
    setView('menu');
    navigate('../menu');
  };

  const canContinue = orderType && (orderType !== 'dine_in' || tableNumber);

  return (
    <div className="kiosk-container min-h-screen flex flex-col">
      {/* Header */}
      <header className="h-20 px-6 flex items-center justify-between border-b border-white/10">
        <IconButton
          icon={ArrowLeft}
          onClick={handleBack}
          aria-label="Go back"
        />
        <div className="text-center">
          <h1 className="text-xl font-semibold text-white font-['Outfit']">
            {restaurant.name || 'Start Your Order'}
          </h1>
        </div>
        <div className="w-14" /> {/* Spacer for alignment */}
      </header>

      {/* Main Content */}
      <main className="flex-1 flex flex-col items-center justify-center px-6 py-8">
        <div className="w-full max-w-2xl">
          
          {/* Order Type Selection */}
          <div className="text-center mb-10">
            <h2 className="text-3xl font-semibold text-white mb-3 font-['Outfit']">
              How would you like to enjoy your meal?
            </h2>
            <p className="text-lg text-gray-400">
              Select an option to continue
            </p>
          </div>

          <div className="grid grid-cols-2 gap-6 mb-10">
            {/* Dine In Option */}
            <button
              onClick={() => handleOrderTypeSelect('dine_in')}
              className={`
                flex flex-col items-center justify-center gap-4
                p-10 rounded-3xl border-2 transition-all duration-200
                ${orderType === 'dine_in'
                  ? 'border-[#f5a623] bg-[#f5a623]/10'
                  : 'border-white/10 bg-[#1a1f26] hover:border-white/20 hover:bg-[#252b33]'
                }
              `}
              aria-pressed={orderType === 'dine_in'}
            >
              <UtensilsCrossed 
                size={64} 
                className={orderType === 'dine_in' ? 'text-[#f5a623]' : 'text-white/70'} 
              />
              <span className="text-2xl font-semibold text-white">Dine In</span>
              <span className="text-base text-gray-400">Eat here</span>
            </button>

            {/* Take Out Option */}
            <button
              onClick={() => handleOrderTypeSelect('pickup')}
              className={`
                flex flex-col items-center justify-center gap-4
                p-10 rounded-3xl border-2 transition-all duration-200
                ${orderType === 'pickup'
                  ? 'border-[#f5a623] bg-[#f5a623]/10'
                  : 'border-white/10 bg-[#1a1f26] hover:border-white/20 hover:bg-[#252b33]'
                }
              `}
              aria-pressed={orderType === 'pickup'}
            >
              <ShoppingBag 
                size={64} 
                className={orderType === 'pickup' ? 'text-[#f5a623]' : 'text-white/70'} 
              />
              <span className="text-2xl font-semibold text-white">Take Out</span>
              <span className="text-base text-gray-400">To go</span>
            </button>
          </div>

          {/* Table Number Input (for Dine In) */}
          {showTableInput && orderType === 'dine_in' && (
            <div className="mb-10 animate-fade-in">
              <div className="text-center mb-6">
                <h3 className="text-xl font-medium text-white mb-2">
                  Enter your table number
                </h3>
                <p className="text-gray-400">
                  Find the number on your table tent
                </p>
              </div>

              {/* Table Number Display */}
              <div className="flex justify-center mb-6">
                <div className="w-48 h-20 flex items-center justify-center rounded-2xl bg-[#1a1f26] border-2 border-white/20">
                  <span className="text-4xl font-bold text-white font-['Outfit']">
                    {tableNumber || '—'}
                  </span>
                </div>
              </div>

              {/* Numpad */}
              <div className="max-w-xs mx-auto grid grid-cols-3 gap-3">
                {[1, 2, 3, 4, 5, 6, 7, 8, 9].map(digit => (
                  <button
                    key={digit}
                    onClick={() => handleTableNumberPress(String(digit))}
                    className="h-16 rounded-xl bg-[#252b33] text-2xl font-semibold text-white hover:bg-[#2d343d] active:scale-95 transition-all"
                  >
                    {digit}
                  </button>
                ))}
                <div /> {/* Empty spacer */}
                <button
                  onClick={() => handleTableNumberPress('0')}
                  className="h-16 rounded-xl bg-[#252b33] text-2xl font-semibold text-white hover:bg-[#2d343d] active:scale-95 transition-all"
                >
                  0
                </button>
                <button
                  onClick={handleTableNumberClear}
                  className="h-16 rounded-xl bg-[#252b33] text-xl font-medium text-gray-400 hover:bg-[#2d343d] active:scale-95 transition-all"
                  aria-label="Backspace"
                >
                  ⌫
                </button>
              </div>
            </div>
          )}

          {/* Loyalty Section */}
          <div className="border-t border-white/10 pt-8">
            {loyaltyInfo ? (
              /* Loyalty Account Connected */
              <div className="flex items-center justify-between p-5 rounded-2xl bg-[#10b981]/10 border border-[#10b981]/30">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-full bg-[#10b981]/20 flex items-center justify-center">
                    <Check size={24} className="text-[#10b981]" />
                  </div>
                  <div>
                    <p className="font-semibold text-white">
                      Welcome back, {loyaltyInfo.customer_name || 'Member'}!
                    </p>
                    <p className="text-sm text-[#10b981]">
                      {loyaltyInfo.points_balance || 0} points available
                    </p>
                  </div>
                </div>
                <button
                  onClick={clearLoyalty}
                  className="p-2 rounded-lg hover:bg-white/10 text-gray-400 hover:text-white transition-colors"
                  aria-label="Remove loyalty account"
                >
                  <X size={20} />
                </button>
              </div>
            ) : showLoyaltyInput ? (
              /* Loyalty Phone Input */
              <div className="animate-fade-in">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-medium text-white">
                    Enter your phone number
                  </h3>
                  <button
                    onClick={() => {
                      setShowLoyaltyInput(false);
                      setLoyaltyError('');
                      setTempPhone('');
                    }}
                    className="text-gray-400 hover:text-white"
                  >
                    Cancel
                  </button>
                </div>

                <div className="flex gap-3">
                  <input
                    type="tel"
                    value={formatPhoneDisplay(tempPhone)}
                    onChange={(e) => handlePhoneInput(e.target.value)}
                    placeholder="(555) 123-4567"
                    className="flex-1 h-14 px-5 rounded-xl bg-[#252b33] border-2 border-white/10 text-white text-lg placeholder-gray-500 focus:border-[#f5a623] focus:outline-none transition-colors"
                    autoFocus
                  />
                  <button
                    onClick={handleLoyaltyLookup}
                    disabled={loyaltyLoading || tempPhone.length < 10}
                    className="h-14 px-6 rounded-xl bg-[#f5a623] text-[#0f1419] font-semibold disabled:opacity-50 disabled:cursor-not-allowed hover:brightness-110 transition-all"
                  >
                    {loyaltyLoading ? 'Checking...' : 'Look Up'}
                  </button>
                </div>

                {loyaltyError && (
                  <p className="mt-3 text-sm text-[#ef4444]">{loyaltyError}</p>
                )}
              </div>
            ) : (
              /* Loyalty CTA */
              <button
                onClick={() => setShowLoyaltyInput(true)}
                className="w-full flex items-center justify-between p-5 rounded-2xl bg-[#1a1f26] border border-white/10 hover:border-white/20 hover:bg-[#252b33] transition-all"
              >
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-full bg-[#f5a623]/10 flex items-center justify-center">
                    <Gift size={24} className="text-[#f5a623]" />
                  </div>
                  <div className="text-left">
                    <p className="font-semibold text-white">Have a rewards account?</p>
                    <p className="text-sm text-gray-400">Earn points on this order</p>
                  </div>
                </div>
                <ChevronRight size={24} className="text-gray-400" />
              </button>
            )}
          </div>
        </div>
      </main>

      {/* Footer - Continue Button */}
      <footer className="p-6 border-t border-white/10">
        <div className="max-w-2xl mx-auto">
          <button
            onClick={handleContinue}
            disabled={!canContinue}
            className={`
              w-full h-16 flex items-center justify-center gap-3
              rounded-2xl font-semibold text-xl transition-all
              ${canContinue
                ? 'bg-gradient-to-r from-[#f5a623] to-[#ff6b35] text-[#0f1419] shadow-lg shadow-[#f5a623]/30 hover:brightness-110 active:scale-[0.99]'
                : 'bg-[#252b33] text-gray-500 cursor-not-allowed'
              }
            `}
          >
            <span>Continue to Menu</span>
            <ChevronRight size={24} />
          </button>

          {orderType === 'dine_in' && !tableNumber && (
            <p className="text-center text-gray-500 mt-3 text-sm">
              Please enter your table number to continue
            </p>
          )}
        </div>
      </footer>

      {/* Animation Styles */}
      <style>{`
        @keyframes fade-in {
          from { opacity: 0; transform: translateY(10px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .animate-fade-in {
          animation: fade-in 0.3s ease-out forwards;
        }
      `}</style>
    </div>
  );
}
